import { max, min } from "lodash"
import { SerpContextData } from "../../../../../aggregators/serp/types"
import {
  SerpSettingsSidebarChip,
  SerpSettingsSidebarFilter,
} from "../../../../../contents/settings/serp/types"
import { ProductCategoryMappingsDict } from "../../../../../types/serp"
import { Tag } from "../../../../../types/tag"
import { SearchState } from "../../../types"
import { FacetOption } from "../../filters/types"
import {
  buildCategoriesTree,
  CategoriesTree,
  getCategoryLabelOverride,
} from "./categories"
import {
  AlgoliaCheckboxCategory,
  AlgoliaCheckboxOption,
  AlgoliaPriceRangeFilter,
  AlgoliaUiFilters,
} from "./types"

export interface CreateAlgoliaFiltersInput {
  serpData: SerpContextData
  searchParams: SearchState
  searchResponses: any
  customCategories?: ProductCategoryMappingsDict
}

const createChipFilter = (
  item: SerpSettingsSidebarChip,
  searchParams: SearchState,
  searchResponses: any,
  customCategories: ProductCategoryMappingsDict | undefined
): AlgoliaCheckboxOption => {
  const code = item.tagCategory?.code ?? ""
  return {
    label:
      getCategoryLabelOverride(customCategories, item.tagCategory?.code) ??
      item.tagCategory?.name ??
      "",
    count: searchResponses[1].facets?.switch?.[code] ?? 0,
    disabled: (searchResponses[0].facets?.switch?.[code] ?? 0) <= 0,
    selected: !!searchParams.switch?.get(code),
    key: code,
    value: code,
  }
}

const createChipFilters = (input: CreateAlgoliaFiltersInput) =>
  input.serpData.settings.data?.sidebar?.chips
    ?.map((x) =>
      createChipFilter(
        x,
        input.searchParams,
        input.searchResponses,
        input.customCategories
      )
    )
    .filter((x) => x.count > 0) ?? []

const createBrandOptions = (
  searchParams: SearchState,
  searchResponses: any
): FacetOption[] => {
  const options: FacetOption[] = []
  const brandFacetResponse = searchResponses.find(
    (x: any) =>
      x.facets && Object.keys(x.facets).length === 1 && !!x.facets.brandName
  )
  for (const key in searchResponses[1]?.facets?.brandName ?? []) {
    const count =
      (brandFacetResponse ?? searchResponses[0])?.facets?.brandName?.[key] ?? 0
    options.push({
      label: key,
      value: `brandName:${key}`,
      count,
      selected: searchParams.brandNames?.includes(`brandName:${key}`) ?? false,
      disabled: count === 0,
    })
  }
  return options
}

const getFacetIntKeys = (facet: any) =>
  facet ? Object.keys(facet).map((x) => parseInt(x)) : []

const createPriceOptions = (searchResponses: any): AlgoliaPriceRangeFilter => {
  return {
    min:
      min(getFacetIntKeys(searchResponses[1]?.facets["inventory.price"])) ?? 0,
    max:
      max(getFacetIntKeys(searchResponses[1]?.facets["inventory.price"])) ?? 0,
  }
}

const createDiscountOptions = (
  searchParams: SearchState,
  searchResponses: any
): FacetOption[] => {
  const options: FacetOption[] = []
  const discountFacetResponse = searchResponses.find(
    (x: any) =>
      x.facets &&
      Object.keys(x.facets).length === 1 &&
      !!x.facets["inventory.discountPercent"]
  )
  for (const key in searchResponses[1]?.facets?.["inventory.discountPercent"] ??
    []) {
    const count =
      (discountFacetResponse ?? searchResponses[0])?.facets?.[
        "inventory.discountPercent"
      ]?.[key] ?? 0
    options.push({
      label: `${key}%`,
      value: `inventory.discountPercent:${key}`,
      count,
      selected:
        searchParams?.discountPercentValues
          ?.map((x) => x.toString())
          .includes(key) ?? false,
      disabled: count === 0,
    })
  }

  if (options.length <= 1) {
    return []
  }

  return options.filter((x) => x.value !== "inventory.discountPercent:0")
}

const createTagFilterCategory = (
  filter: SerpSettingsSidebarFilter,
  searchParams: SearchState,
  searchResponses: any
): AlgoliaCheckboxCategory => {
  const key = `filters.${filter.tagCategory?.code}`
  const facet = searchResponses[1].facets[key]
  if (!facet) {
    return {
      key: "",
      categoryCode: "",
      label: "",
      options: [],
      hasActiveOptions: false,
    }
  }

  const currentSearchFacetResp = searchResponses[0].facets[key]
  const filterFacetResponse = searchResponses.find(
    (x: any) =>
      x.facets && Object.keys(x.facets).length === 1 && !!x.facets[key]
  )
  const filteredFacet = filterFacetResponse?.facets[key]

  const options = Object.keys(facet).map((x) => ({
    selected: searchParams.filters?.get(key)?.includes(x) ?? false,
    disabled: (filteredFacet?.[x] ?? currentSearchFacetResp?.[x] ?? 0) <= 0,
    label: x,
    key,
    value: x,
    count: filteredFacet?.[x] ?? currentSearchFacetResp?.[x] ?? 0,
  }))

  return {
    key,
    categoryCode: filter.tagCategory?.code ?? "",
    label: filter.tagCategory?.name ?? "",
    options,
    hasActiveOptions: options.some((x) => !x.disabled),
  }
}

const createTagFilters = (
  searchParams: SearchState,
  searchResponses: any,
  serpData: SerpContextData
): AlgoliaCheckboxCategory[] => {
  const categories: AlgoliaCheckboxCategory[] = []
  for (const category of serpData.settings.data?.sidebar?.filters?.filter(
    (x) => x.tagCategory
  ) ?? []) {
    const item = createTagFilterCategory(
      category,
      searchParams,
      searchResponses
    )
    if (item.options.length > 0) {
      categories.push(item)
    }
  }

  return categories
}

const createCategoriesTree = (
  searchParams: SearchState,
  searchResponses: any,
  serpData: SerpContextData,
  customCategories: ProductCategoryMappingsDict | undefined
): CategoriesTree => {
  return buildCategoriesTree(
    serpData.categories,
    searchResponses,
    searchParams,
    customCategories
  )
}

export const createAlgoliaUiFilters = (
  input: CreateAlgoliaFiltersInput
): AlgoliaUiFilters => {
  return {
    chips: createChipFilters(input),
    brands: createBrandOptions(input.searchParams, input.searchResponses),
    price: createPriceOptions(input.searchResponses),
    discounts: createDiscountOptions(input.searchParams, input.searchResponses),
    tags: createTagFilters(
      input.searchParams,
      input.searchResponses,
      input.serpData
    ),
    categories: createCategoriesTree(
      input.searchParams,
      input.searchResponses,
      input.serpData,
      input.customCategories
    ),
  }
}

export const normalizeTag = (tag: Tag): Tag => ({
  code: tag.code.toLowerCase(),
  key: tag.key.toLowerCase(),
})
